import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Info, AlertCircle } from "lucide-react";
import MortgageChainFooter from "../../components/MortgageChainFooter/MortgageChainFooter";

export default function MortgageChainRiskDisclosures() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const emitFormEvent = (type: "call" | "deck") => {
    window.dispatchEvent(
      new CustomEvent("mortgagechain:openForm", { detail: { type } })
    );
  };

  return (
    <div className="bg-[#0A0E27] text-white min-h-screen">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;600;700&family=Inter:wght@400;500;600&display=swap');
        * { font-family: 'Inter', sans-serif; }
        h1, h2, h3 { font-family: 'Space Grotesk', sans-serif; }
        .glassmorphic { background: rgba(26,31,58,0.8); backdrop-filter: blur(20px); border: 1px solid rgba(0,212,255,0.2); }
        .gradient-text { background: linear-gradient(90deg,#00D4FF 0%,#8B5CF6 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .risk-level { display: inline-block; padding: 4px 12px; border-radius: 6px; font-size: 12px; font-weight: 600; text-transform: uppercase; margin-left: 10px; }
        .risk-high { background: rgba(220, 38, 38, 0.2); color: #fca5a5; border: 1px solid rgba(220, 38, 38, 0.4); }
        .risk-medium { background: rgba(245, 158, 11, 0.2); color: #fcd34d; border: 1px solid rgba(245, 158, 11, 0.4); }
        .risk-low { background: rgba(37, 99, 235, 0.2); color: #93c5fd; border: 1px solid rgba(37, 99, 235, 0.4); }
      `}</style>

      <section className="relative pt-28 pb-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-cyan-500/10 to-purple-500/10 border border-cyan-500/30 backdrop-blur-sm mb-6">
              <AlertTriangle className="w-4 h-4 text-cyan-400" />
              <span className="text-sm font-semibold text-cyan-400">
                Risk Disclosures
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">
              Risk <span className="gradient-text">Disclosures</span>
            </h1>
            <p className="text-gray-400 text-lg">
              Last Updated: January 15, 2025
            </p>
          </div>

          <div className="glassmorphic rounded-2xl p-8 mb-8">
            <div className="bg-red-500/10 border-l-4 border-red-500 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2 text-red-400">
                <AlertCircle className="w-5 h-5" />
                CRITICAL NOTICE - READ CAREFULLY
              </h3>
              <p className="text-red-200">
                The data and information provided by Crypsey are for
                informational and analytical purposes only. Crypsey DOES NOT
                provide investment advice, financial planning services, or
                recommendations to buy, sell, or hold any securities or
                investments. You are solely responsible for your investment
                decisions and must conduct your own due diligence. Past
                performance does not guarantee future results. All investments
                carry risk, including the potential loss of principal.
              </p>
            </div>

            <div className="space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  1. Overview
                </h2>
                <p className="text-gray-300">
                  This Risk Disclosure document outlines the material risks
                  associated with using Crypsey&apos;s mortgage data platform
                  and making investment decisions based on the data provided. By
                  using our platform, you acknowledge that you understand these
                  risks and accept full responsibility for your decisions.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  2. No Investment Advice{" "}
                  <span className="risk-level risk-high">Critical</span>
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  2.1 Data Platform Only
                </h3>
                <p className="text-gray-300 mb-3">
                  Crypsey provides raw data, analytics, and market insights. We
                  do NOT:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Provide personalized investment advice or recommendations
                  </li>
                  <li>Suggest specific investments or strategies</li>
                  <li>Act as a financial advisor, broker, or dealer</li>
                  <li>Endorse any particular investment opportunity</li>
                  <li>Guarantee returns or predict future performance</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  2.2 Consult Qualified Professionals
                </h3>
                <p className="text-gray-300 mb-3">
                  Before making any investment decision, you should:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Consult with a licensed financial advisor</li>
                  <li>
                    Seek legal and tax advice from qualified professionals
                  </li>
                  <li>Conduct independent due diligence</li>
                  <li>
                    Consider your personal financial situation, goals, and risk
                    tolerance
                  </li>
                  <li>Review all relevant documentation and disclosures</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  2.3 Your Responsibility
                </h3>
                <p className="text-gray-300 mb-3">
                  You are solely responsible for:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>All investment decisions you make</li>
                  <li>Evaluating whether any investment is suitable for you</li>
                  <li>Understanding the risks of any investment</li>
                  <li>Losses resulting from your decisions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  3. Data Limitations &amp; Accuracy Risks{" "}
                  <span className="risk-level risk-high">Critical</span>
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  3.1 Data Completeness
                </h3>
                <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-bold mb-2 flex items-center gap-2 text-amber-200">
                    <AlertTriangle className="w-5 h-5" />
                    Limited Market Coverage
                  </h4>
                  <p className="text-amber-200 mb-3">
                    Our data represents only a portion of Canada&apos;s mortgage
                    market. We do not capture:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-amber-200 ml-4">
                    <li>All mortgage originators (limited data partners)</li>
                    <li>All geographic regions equally</li>
                    <li>All borrower types and loan products</li>
                    <li>Major banks and their prime mortgage portfolios</li>
                  </ul>
                </div>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  3.2 Data Accuracy
                </h3>
                <p className="text-gray-300 mb-3">
                  <strong className="text-white">
                    No Guarantee of Accuracy:
                  </strong>{" "}
                  While we implement verification processes, we cannot
                  guarantee:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Complete Accuracy:</strong>{" "}
                    Data may contain errors, omissions, or inaccuracies
                  </li>
                  <li>
                    <strong className="text-white">Real-Time Updates:</strong>{" "}
                    Data may be delayed by days or weeks
                  </li>
                  <li>
                    <strong className="text-white">Source Quality:</strong> We
                    rely on data partners; errors may exist in source data
                  </li>
                  <li>
                    <strong className="text-white">Standardization:</strong>{" "}
                    Different lenders may report data differently
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  3.3 Sampling Bias
                </h3>
                <p className="text-gray-300 mb-3">
                  Our data may be biased toward:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Alternative lenders (higher risk profiles)</li>
                  <li>Certain geographic markets</li>
                  <li>Specific property types or loan sizes</li>
                  <li>Lenders who choose to share data with us</li>
                </ul>
                <p className="text-gray-300">
                  <strong className="text-white">Risk:</strong> Conclusions
                  drawn from our data may not apply to the broader market.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  3.4 Anonymization Limitations
                </h3>
                <p className="text-gray-300 mb-3">
                  While all data is anonymized, there are inherent limitations:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Statistical outliers may potentially be identifiable with
                    external data
                  </li>
                  <li>
                    Geographic data (postal prefixes) provides general location
                  </li>
                  <li>
                    You must not attempt to re-identify individuals (violation
                    of Terms)
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  4. Investment &amp; Market Risks{" "}
                  <span className="risk-level risk-high">Critical</span>
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.1 Mortgage Investment Risks
                </h3>
                <p className="text-gray-300 mb-3">
                  Mortgage investments carry substantial risks:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Default Risk:</strong>{" "}
                    Borrowers may fail to repay loans, resulting in losses
                  </li>
                  <li>
                    <strong className="text-white">Liquidity Risk:</strong>{" "}
                    Mortgage investments may be difficult or impossible to sell
                    quickly
                  </li>
                  <li>
                    <strong className="text-white">Interest Rate Risk:</strong>{" "}
                    Rising rates can decrease value of existing mortgages
                  </li>
                  <li>
                    <strong className="text-white">Property Value Risk:</strong>{" "}
                    Real estate values may decline, affecting loan-to-value
                    ratios
                  </li>
                  <li>
                    <strong className="text-white">Prepayment Risk:</strong>{" "}
                    Borrowers may repay early, affecting expected returns
                  </li>
                  <li>
                    <strong className="text-white">Economic Risk:</strong>{" "}
                    Recessions can increase defaults and decrease property
                    values
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.2 Alternative Lending Risks
                </h3>
                <p className="text-gray-300 mb-3">
                  Alternative/private mortgages carry additional risks:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Higher default rates than prime mortgages</li>
                  <li>Less regulatory oversight</li>
                  <li>Limited borrower protections</li>
                  <li>Higher sensitivity to economic downturns</li>
                  <li>Potential for fraud or misrepresentation</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.3 Past Performance
                </h3>
                <div className="bg-red-500/10 border-l-4 border-red-500 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-bold mb-2 text-red-400">
                    PAST PERFORMANCE DOES NOT GUARANTEE FUTURE RESULTS
                  </h4>
                  <p className="text-red-200">
                    Historical data shows what happened in the past, not what
                    will happen in the future. Market conditions, economic
                    factors, and borrower behavior can change dramatically. DO
                    NOT assume that historical trends will continue.
                  </p>
                </div>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  4.4 Loss of Principal
                </h3>
                <p className="text-gray-300 mb-3">
                  All investments carry the risk of loss, including:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Total Loss:</strong> You
                    could lose 100% of your investment
                  </li>
                  <li>
                    <strong className="text-white">Partial Loss:</strong> You
                    may recover less than your initial investment
                  </li>
                  <li>
                    <strong className="text-white">Opportunity Cost:</strong>{" "}
                    Capital tied up in poor investments cannot be deployed
                    elsewhere
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  5. Technology &amp; Operational Risks{" "}
                  <span className="risk-level risk-medium">Medium</span>
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.1 Platform Availability
                </h3>
                <p className="text-gray-300 mb-3">
                  Technical issues may affect your ability to access data:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>System outages or maintenance downtime</li>
                  <li>API rate limits or throttling</li>
                  <li>Internet connectivity issues</li>
                  <li>
                    Third-party infrastructure failures (AWS, blockchain
                    networks)
                  </li>
                </ul>
                <p className="text-gray-300">
                  <strong className="text-white">Impact:</strong> Inability to
                  access critical data when needed for time-sensitive decisions.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.2 Data Security Risks
                </h3>
                <p className="text-gray-300 mb-3">
                  Despite our security measures, risks include:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Potential data breaches (affecting your account information)
                  </li>
                  <li>Unauthorized access to your account</li>
                  <li>Cyber attacks or hacking attempts</li>
                  <li>Loss of API keys or credentials</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.3 Blockchain Risks
                </h3>
                <p className="text-gray-300 mb-3">
                  Our blockchain verification feature carries specific risks:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Network Risks:</strong>{" "}
                    Solana blockchain may experience downtime or congestion
                  </li>
                  <li>
                    <strong className="text-white">
                      Smart Contract Risks:
                    </strong>{" "}
                    Potential bugs or vulnerabilities in code
                  </li>
                  <li>
                    <strong className="text-white">Limited Recourse:</strong>{" "}
                    Blockchain transactions are irreversible
                  </li>
                  <li>
                    <strong className="text-white">
                      Regulatory Uncertainty:
                    </strong>{" "}
                    Evolving regulations around blockchain technology
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  5.4 Business Continuity
                </h3>
                <p className="text-gray-300 mb-3">
                  Crypsey is an early-stage company. Risks include:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Company may cease operations</li>
                  <li>Service may be discontinued</li>
                  <li>Features may be reduced or changed</li>
                  <li>Pricing may change significantly</li>
                  <li>Company may be acquired, affecting service quality</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  6. Regulatory &amp; Compliance Risks{" "}
                  <span className="risk-level risk-medium">Medium</span>
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  6.1 Regulatory Changes
                </h3>
                <p className="text-gray-300 mb-3">
                  Mortgage and financial data industries are subject to
                  regulation:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Privacy Laws:</strong>{" "}
                    Changes to PIPEDA or new privacy regulations could affect
                    operations
                  </li>
                  <li>
                    <strong className="text-white">
                      Financial Regulations:
                    </strong>{" "}
                    New rules may restrict data collection or distribution
                  </li>
                  <li>
                    <strong className="text-white">Securities Laws:</strong>{" "}
                    Regulations may limit how data can be used for investment
                    decisions
                  </li>
                  <li>
                    <strong className="text-white">
                      Blockchain Regulations:
                    </strong>{" "}
                    Evolving crypto/blockchain regulations may impact our
                    verification features
                  </li>
                </ul>
                <p className="text-gray-300">
                  <strong className="text-white">Impact:</strong> Service
                  features may change, costs may increase, or certain services
                  may become unavailable.
                </p>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  6.2 Your Regulatory Obligations
                </h3>
                <p className="text-gray-300 mb-3">
                  You are responsible for complying with all applicable
                  regulations:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Securities laws if you use data for investment management
                  </li>
                  <li>Privacy laws if you handle any personal information</li>
                  <li>Anti-money laundering (AML) requirements</li>
                  <li>Know Your Customer (KYC) rules</li>
                  <li>
                    Licensing requirements for your profession or industry
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  7. Reliance &amp; Interpretation Risks{" "}
                  <span className="risk-level risk-medium">Medium</span>
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  7.1 Over-Reliance on Data
                </h3>
                <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-bold mb-2 flex items-center gap-2 text-amber-200">
                    <AlertTriangle className="w-5 h-5" />
                    Do Not Rely Solely on Crypsey Data
                  </h4>
                  <p className="text-amber-200 mb-3">
                    Our data should be one of many inputs in your
                    decision-making process. Over-reliance on any single data
                    source is dangerous. Always:
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-amber-200 ml-4">
                    <li>Use multiple data sources and perspectives</li>
                    <li>Conduct independent research and due diligence</li>
                    <li>Verify critical information through primary sources</li>
                    <li>Consider qualitative factors not captured in data</li>
                  </ul>
                </div>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  7.2 Interpretation Errors
                </h3>
                <p className="text-gray-300 mb-3">
                  Misinterpreting data can lead to poor decisions:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">
                      Correlation vs Causation:
                    </strong>{" "}
                    Data may show patterns that don&apos;t indicate causation
                  </li>
                  <li>
                    <strong className="text-white">
                      Statistical Significance:
                    </strong>{" "}
                    Small sample sizes may produce misleading results
                  </li>
                  <li>
                    <strong className="text-white">Context Missing:</strong>{" "}
                    Data alone may not capture full market dynamics
                  </li>
                  <li>
                    <strong className="text-white">Confirmation Bias:</strong>{" "}
                    You may interpret data to confirm pre-existing beliefs
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  7.3 Model Risk
                </h3>
                <p className="text-gray-300 mb-3">
                  If you build models or algorithms using our data:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Models are simplifications and may be wrong</li>
                  <li>Past patterns may not repeat in the future</li>
                  <li>Models may overfit to historical data</li>
                  <li>Unexpected events (black swans) can invalidate models</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  8. Third-Party &amp; Counterparty Risks{" "}
                  <span className="risk-level risk-medium">Medium</span>
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  8.1 Data Partner Risks
                </h3>
                <p className="text-gray-300 mb-3">
                  We depend on data partners (lenders, brokers) who may:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Provide inaccurate or incomplete data</li>
                  <li>Stop sharing data, reducing our coverage</li>
                  <li>Change reporting methodologies</li>
                  <li>Experience their own operational issues</li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  8.2 Service Provider Risks
                </h3>
                <p className="text-gray-300 mb-3">
                  We rely on third-party service providers:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">AWS:</strong> Cloud
                    infrastructure outages could disrupt service
                  </li>
                  <li>
                    <strong className="text-white">Solana:</strong> Blockchain
                    network issues could affect verification
                  </li>
                  <li>
                    <strong className="text-white">Payment Processors:</strong>{" "}
                    Issues could affect billing or refunds
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  8.3 Investment Counterparty Risk
                </h3>
                <p className="text-gray-300 mb-3">
                  If you use our data to evaluate investment opportunities:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Mortgage originators may default on their obligations</li>
                  <li>Investment vehicles may be mismanaged</li>
                  <li>Fraud may exist at the originator or borrower level</li>
                  <li>
                    Our data cannot detect all forms of fraud or misconduct
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  9. Specific Use Case Risks
                </h2>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.1 For Investors &amp; Fund Managers
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Data may not reflect the specific loans you&apos;re
                    evaluating
                  </li>
                  <li>
                    Market-wide data may not apply to individual investments
                  </li>
                  <li>
                    Regulatory requirements for your investment decisions still
                    apply
                  </li>
                  <li>
                    You must conduct independent underwriting and due diligence
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.2 For Lenders &amp; Originators
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    Benchmarking data may not reflect your specific borrower
                    profile
                  </li>
                  <li>Pricing based solely on our data may be inappropriate</li>
                  <li>
                    Underwriting standards must be based on comprehensive
                    analysis
                  </li>
                  <li>
                    Regulatory compliance is your responsibility, not ours
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.3 For PropTech &amp; Fintech Companies
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>APIs may change, affecting your integrations</li>
                  <li>
                    Rate limits may restrict your application&apos;s
                    functionality
                  </li>
                  <li>Data coverage may not match your target market</li>
                  <li>
                    You are responsible for how you present data to your users
                  </li>
                </ul>

                <h3 className="text-xl font-bold mt-6 mb-3 text-white">
                  9.4 For Researchers &amp; Analysts
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>Sampling bias may affect research conclusions</li>
                  <li>
                    Data may not be suitable for academic publication without
                    caveats
                  </li>
                  <li>
                    Confidentiality restrictions apply to non-aggregated data
                  </li>
                  <li>Attribution to Crypsey required for public research</li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  10. Cryptocurrency Payment Risks{" "}
                  <span className="risk-level risk-low">Low</span>
                </h2>
                <p className="text-gray-300 mb-3">
                  If you choose to pay with cryptocurrency (USDC/SOL):
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Price Volatility:</strong>{" "}
                    Crypto prices can fluctuate significantly
                  </li>
                  <li>
                    <strong className="text-white">Irreversibility:</strong>{" "}
                    Crypto transactions cannot be reversed
                  </li>
                  <li>
                    <strong className="text-white">Wallet Security:</strong> You
                    are responsible for wallet security
                  </li>
                  <li>
                    <strong className="text-white">Tax Implications:</strong>{" "}
                    Crypto payments may have tax consequences
                  </li>
                  <li>
                    <strong className="text-white">Regulatory Risk:</strong>{" "}
                    Crypto regulations may change
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  11. Acknowledgment of Risks
                </h2>
                <p className="text-gray-300 mb-3">
                  By using Crypsey&apos;s platform, you acknowledge that:
                </p>
                <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    You have read and understood all risks disclosed in this
                    document
                  </li>
                  <li>
                    You accept full responsibility for your investment and
                    business decisions
                  </li>
                  <li>
                    You will not rely solely on Crypsey data for decision-making
                  </li>
                  <li>You will conduct independent due diligence</li>
                  <li>
                    You will consult qualified professionals before making
                    financial decisions
                  </li>
                  <li>
                    You understand that you could lose money based on decisions
                    informed by our data
                  </li>
                  <li>
                    You will not hold Crypsey liable for losses resulting from
                    your decisions
                  </li>
                  <li>You understand the limitations and biases in our data</li>
                </ol>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  12. No Guarantee of Profitability
                </h2>
                <div className="bg-red-500/10 border-l-4 border-red-500 rounded-lg p-4 mb-4">
                  <h4 className="text-lg font-bold mb-2 text-red-400">
                    NO GUARANTEED RETURNS
                  </h4>
                  <p className="text-red-200">
                    Crypsey makes NO guarantees, representations, or warranties
                    regarding the profitability of any investment strategy,
                    decision, or action based on our data. Historical returns,
                    if shown, are not indicative of future performance. There is
                    NO guarantee that any investment will be profitable or that
                    you will not lose money.
                  </p>
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  13. Updates to Risk Disclosures
                </h2>
                <p className="text-gray-300">
                  We may update these Risk Disclosures from time to time.
                  Material changes will be communicated via email. Your
                  continued use of the platform after updates constitutes
                  acceptance of the revised disclosures.
                </p>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-4 text-cyan-400 border-b border-cyan-500/30 pb-3">
                  14. Questions or Concerns
                </h2>
                <p className="text-gray-300 mb-3">
                  If you have questions about these risk disclosures or need
                  clarification:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">Email:</strong>{" "}
                    <a
                      href="mailto:patrick@crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      patrick@crypsey.com
                    </a>
                  </li>
                  <li>
                    <strong className="text-white">General Support:</strong>{" "}
                    <a
                      href="mailto:patrick@crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      patrick@crypsey.com
                    </a>
                  </li>
                  <li>
                    <strong className="text-white">Legal Inquiries:</strong>{" "}
                    <a
                      href="mailto:patrick@crypsey.com"
                      className="text-cyan-400 hover:text-cyan-300"
                    >
                      patrick@crypsey.com
                    </a>
                  </li>
                </ul>
              </section>

              <div className="bg-cyan-500/10 border-l-4 border-cyan-500 rounded-lg p-6 mt-8">
                <h3 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                  <Info className="w-5 h-5" />
                  Important Reminders
                </h3>
                <ul className="list-disc list-inside space-y-2 text-gray-300 ml-4">
                  <li>
                    <strong className="text-white">
                      Consult Professionals:
                    </strong>{" "}
                    Always seek advice from qualified financial, legal, and tax
                    advisors
                  </li>
                  <li>
                    <strong className="text-white">Do Your Research:</strong>{" "}
                    Conduct thorough due diligence beyond our data
                  </li>
                  <li>
                    <strong className="text-white">
                      Understand Your Investments:
                    </strong>{" "}
                    Never invest in something you don&apos;t fully understand
                  </li>
                  <li>
                    <strong className="text-white">Manage Risk:</strong>{" "}
                    Diversify investments and never invest more than you can
                    afford to lose
                  </li>
                  <li>
                    <strong className="text-white">Stay Informed:</strong>{" "}
                    Market conditions change; continuously educate yourself
                  </li>
                  <li>
                    <strong className="text-white">Know Your Limits:</strong>{" "}
                    Understand your risk tolerance and investment timeline
                  </li>
                </ul>
              </div>

              <div className="bg-red-500/10 border-l-4 border-red-500 rounded-lg p-6 mt-6">
                <h3 className="text-xl font-bold mb-3 text-red-400 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  FINAL WARNING
                </h3>
                <p className="text-red-200">
                  <strong className="text-red-400">
                    ALL INVESTMENTS CARRY RISK. YOU COULD LOSE MONEY.
                  </strong>
                  <br />
                  <br />
                  Crypsey provides data and analytics tools. We do NOT provide
                  investment advice, manage investments, or guarantee outcomes.
                  You are solely responsible for your financial decisions and
                  their consequences. If you are not comfortable with these
                  risks, DO NOT use our platform to make investment decisions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MortgageChainFooter
        onScheduleCall={() => emitFormEvent("call")}
        onViewDeck={() => emitFormEvent("deck")}
      />
    </div>
  );
}
